import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { AlertTriangle, Copy } from 'lucide-react';
import { decodeSignal, computeRawFromPhysical, computeRawRange } from '../../../lib/canEndianness/decode';
import { buildDbcLine } from '../../../lib/canEndianness/dbc';
import { colorForIndex } from '../../../lib/canEndianness/defaults';

const copyText = async (text, successMessage) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(successMessage);
  } catch {
    toast.error('Could not copy to clipboard.');
  }
};

const formatNum = (value) => {
  if (!Number.isFinite(value)) return '—';
  const rounded = Math.round(value * 10000) / 10000;
  return rounded.toString();
};

const SignalResultCard = ({ signal, index, dataBytes, isSelected, isOverlapping, isOutOfRange, onSelect, onPhysicalEdit }) => {
  const result = decodeSignal(dataBytes, signal);
  const dbcLine = buildDbcLine(signal);
  const rawRange = computeRawRange(signal);
  const problem = isOverlapping || isOutOfRange;

  const [physicalDraft, setPhysicalDraft] = useState(formatNum(result.physical));
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!editing) setPhysicalDraft(formatNum(result.physical));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.physical, editing]);

  const commitPhysicalEdit = () => {
    setEditing(false);
    const parsed = parseFloat(physicalDraft);
    if (!Number.isFinite(parsed)) {
      setPhysicalDraft(formatNum(result.physical));
      return;
    }
    if (parsed === result.physical) return;
    const { clamped } = computeRawFromPhysical(signal, parsed);
    onPhysicalEdit(signal.id, parsed);
    if (clamped) toast.warning(`${signal.name || 'Signal'}: value clamped to its representable range.`);
  };

  // Raw = (Physical - Offset) / Factor, shown as an explicit worked calculation
  // alongside the resulting hex/binary — the reverse of the decode direction.
  const rawFromPhysicalFloat = (result.physical - signal.offset) / signal.factor;

  return (
    <div
      onClick={() => onSelect(isSelected ? null : signal.id)}
      className={`rounded-lg border p-4 space-y-3 cursor-pointer transition-colors ${
        isSelected ? 'border-autoxium-blue ring-1 ring-autoxium-blue bg-autoxium-blue/5' : 'border-structure hover:border-autoxium-blue/50'
      }`}
      data-testid={`can-endianness-result-${signal.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ backgroundColor: colorForIndex(index) }} />
          <span className="font-chivo font-bold uppercase tracking-tight truncate">{signal.name || 'Unnamed'}</span>
        </div>
        <Badge variant="outline" className="font-mono text-[9px] uppercase shrink-0">
          {signal.byteOrder === 'motorola' ? 'Motorola' : 'Intel'}
        </Badge>
      </div>

      {problem && (
        <div className="flex items-center gap-1.5 text-xs text-destructive" data-testid={`can-endianness-result-warning-${signal.id}`}>
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          {isOverlapping ? 'Overlaps another signal — value below is not meaningful.' : 'Exceeds current DLC — value below is not meaningful.'}
        </div>
      )}

      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs font-mono">
        <span className="text-muted-foreground">Start Bit</span><span>{signal.startBit}</span>
        <span className="text-muted-foreground">Length</span><span>{signal.length} bit{signal.length === 1 ? '' : 's'}</span>
        <span className="text-muted-foreground">Type</span><span>{signal.signed ? 'Signed' : 'Unsigned'}</span>
        <span className="text-muted-foreground">Factor</span><span>{formatNum(signal.factor)}</span>
        <span className="text-muted-foreground">Offset</span><span>{formatNum(signal.offset)}</span>
      </div>

      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs font-mono pt-1 border-t border-structure/60">
        <span className="text-muted-foreground">Physical Min</span><span>{formatNum(signal.physicalMin)} {signal.unit}</span>
        <span className="text-muted-foreground">Physical Max</span><span>{formatNum(signal.physicalMax)} {signal.unit}</span>
        <span className="text-muted-foreground">Raw Min</span><span>{rawRange.min.toString()}</span>
        <span className="text-muted-foreground">Raw Max</span><span>{rawRange.max.toString()}</span>
      </div>

      <div className="pt-1 border-t border-structure/60 space-y-1">
        <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Physical Value (editable)</span>
        <div className="flex items-center gap-2">
          <Input
            value={physicalDraft}
            onFocus={() => setEditing(true)}
            onChange={(e) => setPhysicalDraft(e.target.value)}
            onBlur={commitPhysicalEdit}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            onClick={(e) => e.stopPropagation()}
            className="font-mono text-xl font-black text-autoxium-blue border-0 shadow-none px-0 h-auto focus-visible:ring-1 focus-visible:ring-autoxium-blue max-w-[10rem]"
            data-testid={`can-endianness-physical-input-${signal.id}`}
          />
          <span className="text-sm font-bold text-muted-foreground">{signal.unit}</span>
        </div>
      </div>

      <div className="rounded-md bg-muted/40 px-3 py-2 space-y-1" data-testid={`can-endianness-conversion-${signal.id}`}>
        <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Conversion: Physical → Raw</p>
        <p className="text-xs font-mono">
          ({formatNum(result.physical)} − {formatNum(signal.offset)}) ÷ {formatNum(signal.factor)} = {formatNum(rawFromPhysicalFloat)} → <span className="font-bold">{result.raw.toString()}</span>
        </p>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs font-mono pt-1">
          <span className="text-muted-foreground">Hex</span><span>{result.hex}</span>
          <span className="text-muted-foreground">Binary</span><span className="break-all">{result.binary}</span>
        </div>
      </div>

      <div className="flex items-center gap-2 bg-muted/40 rounded-md px-2 py-1.5">
        <code className="text-[11px] font-mono flex-1 min-w-0 break-words">{dbcLine}</code>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0"
          onClick={(e) => { e.stopPropagation(); copyText(dbcLine, `DBC definition for ${signal.name || 'signal'} copied.`); }}
          aria-label="Copy DBC definition"
          data-testid={`can-endianness-copy-dbc-${signal.id}`}
        >
          <Copy className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};

export default SignalResultCard;
